// Tipos e defaults dos estágios do funil. Desde a Fase 2, o array efetivo
// vem do backend kanban_api (store `kanban-config`). Estes defaults são
// usados como fallback se o backend estiver indisponível no boot.

export interface StageSLA {
  warningHours: number
  dangerHours: number
}

export type StageVisibility = 'all' | 'assigned_only' | 'supervisors_only'

export interface Stage {
  key: string
  label: string
  sla: StageSLA | null
  visibility: StageVisibility
}

// Keys são códigos hex opacos (`s_xxxxxx`) intencionalmente — labels e
// ordem podem mudar livremente via SettingsView sem invalidar dados
// existentes no Chatwoot. NÃO recriar como strings descritivas/sequenciais.
export const DEFAULT_STAGES: readonly Stage[] = [
  {
    key: 's_5412d6',
    label: 'Novo',
    sla: { warningHours: 4, dangerHours: 24 },
    visibility: 'all',
  },
  {
    key: 's_767188',
    label: 'Qualificando',
    sla: { warningHours: 4, dangerHours: 24 },
    visibility: 'all',
  },
  {
    key: 's_a6f09d',
    label: 'Em atendimento',
    sla: { warningHours: 12, dangerHours: 48 },
    visibility: 'all',
  },
  {
    key: 's_230ee6',
    label: 'Aguardando cliente',
    sla: { warningHours: 48, dangerHours: 168 },
    visibility: 'all',
  },
  {
    key: 's_17f2cd',
    label: 'Proposta enviada',
    sla: { warningHours: 24, dangerHours: 72 },
    visibility: 'all',
  },
  { key: 's_dac54b', label: 'Ganho', sla: null, visibility: 'all' },
  { key: 's_6a9048', label: 'Perdido', sla: null, visibility: 'all' },
] as const

export const DEFAULT_FALLBACK_KEY = 's_5412d6'

// Em runtime, stages podem ser qualquer string (vêm do backend). Os
// componentes recebem o objeto Stage como prop; chaves estão validadas
// server-side (regex em validateConfig).
export type StageKey = string

// Resolve um valor desconhecido (do custom_attribute.kanban_stage) para uma
// chave válida no array de stages atual. Se inválida, retorna o primeiro
// stage da lista (ou DEFAULT_FALLBACK_KEY se a lista estiver vazia).
export function resolveStage(
  value: unknown,
  stages: readonly Stage[],
): string {
  const fallback = stages[0]?.key ?? DEFAULT_FALLBACK_KEY
  if (typeof value !== 'string') return fallback
  return stages.find((s) => s.key === value) ? value : fallback
}

export function findStage(
  stages: readonly Stage[],
  key: string,
): Stage | undefined {
  return stages.find((s) => s.key === key)
}
